"use client";

import type { MapLocationData } from "@/components/ui/primitives/map-location";
import { toast } from "@/components/ui";
import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

type PermissionStatus = "granted" | "denied" | "prompt" | "unavailable";

interface GeolocationState {
  permission: PermissionStatus;
  location: MapLocationData | null;
  isLoading: boolean;
  error: string | null;
}

interface GeolocationContextValue extends GeolocationState {
  requestLocation: () => Promise<MapLocationData | null>;
  clearError: () => void;
  resetPermission: () => void;
}

export const GeolocationContext = createContext<
  GeolocationContextValue | undefined
>(undefined);

interface GeolocationProviderProps {
  readonly children: ReactNode;
}

/**
 * GeolocationProvider - Centralized geolocation state management
 *
 * Features:
 * - Permission status tracking
 * - Location requests with consistent error handling
 * - Loading and error states
 * - Permission API integration
 */
export function GeolocationProvider({ children }: GeolocationProviderProps) {
  const [state, setState] = useState<GeolocationState>({
    permission: "prompt",
    location: null,
    isLoading: false,
    error: null,
  });

  // Check initial permission status
  useEffect(() => {
    const checkPermission = async () => {
      if (!navigator.geolocation) {
        setState((prev) => ({
          ...prev,
          permission: "unavailable",
          error: "Geolocation is not supported by your browser",
        }));
        return;
      }

      // Check if Permissions API is available
      if (navigator.permissions) {
        try {
          const permissionStatus = await navigator.permissions.query({
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            name: "geolocation" as any,
          });

          setState((prev) => ({
            ...prev,
            permission: permissionStatus.state as PermissionStatus,
            error:
              permissionStatus.state === "denied"
                ? "Location access denied. Please enable location permissions in your browser."
                : null,
          }));

          // Listen for permission changes
          permissionStatus.addEventListener("change", () => {
            setState((prev) => ({
              ...prev,
              permission: permissionStatus.state as PermissionStatus,
              error:
                permissionStatus.state === "denied"
                  ? "Location access denied. Please enable location permissions in your browser."
                  : null,
            }));
          });
        } catch {
          // Permission API not available, we'll handle it when user requests location
        }
      }
    };

    void checkPermission();
  }, []);

  /**
   * Request current device location
   * Returns location data or null on error
   */
  const requestLocation = useCallback((): Promise<MapLocationData | null> => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        setState((prev) => ({
          ...prev,
          permission: "unavailable",
          error: "Geolocation is not supported by your browser",
        }));
        resolve(null);
        return;
      }

      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location: MapLocationData = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            altitude: position.coords.altitude,
            address: null,
          };

          setState((prev) => ({
            ...prev,
            permission: "granted",
            location,
            isLoading: false,
            error: null,
          }));

          resolve(location);
        },
        (error) => {
          // Parse error code
          let errorMessage = "Unable to get your location.";
          let permissionStatus: PermissionStatus = "prompt";

          switch (error.code) {
            case 1: // PERMISSION_DENIED
              errorMessage =
                "Location access denied. Please enable location permissions in your browser.";
              permissionStatus = "denied";
              break;
            case 2: // POSITION_UNAVAILABLE
              errorMessage = "Location information is unavailable.";
              break;
            case 3: // TIMEOUT
              errorMessage = "Location request timed out. Please try again.";
              break;
            default:
              errorMessage = error.message || "Unable to get your location.";
          }

          // Show toast notification for user feedback
          toast.error(errorMessage);

          setState((prev) => ({
            ...prev,
            permission: permissionStatus,
            isLoading: false,
            error: errorMessage,
          }));

          resolve(null);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        },
      );
    });
  }, []); // No dependencies - function is stable

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  /**
   * Reset permission state to "prompt" - useful when user has changed browser settings
   */
  const resetPermission = useCallback(() => {
    setState((prev) => ({ ...prev, permission: "prompt", error: null }));
  }, []);

  const value: GeolocationContextValue = useMemo(
    () => ({
      ...state,
      requestLocation,
      clearError,
      resetPermission,
    }),
    [state, requestLocation, clearError, resetPermission],
  );

  return (
    <GeolocationContext.Provider value={value}>
      {children}
    </GeolocationContext.Provider>
  );
}

export type { GeolocationContextValue, PermissionStatus };
